import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Facet, loadSearchActions, loadSearchAnalyticsActions } from '@coveo/headless';
import SearchLocationHero from './LocationSearchHero/LocationSearchHero';
import LocationSearchFilterBar from './LocationSearchFilterBar/LocationSearchFilterBar';
import MapCoveoResultToCard from './mapCoveoResultToCard/mapCoveoResultToCard';
import {
  LocationSearchCoords,
  LocationSearchFacetControllers,
  LocationSearchFacetKey,
  SearchLocationHeroProps,
} from './LocationSearch.types';
import {
  getLocationSearchDistanceOptions,
  mapSitesFacetsToControllers,
  resolveDefaultDistanceSelection,
} from './locationSearchFilterConfig';
import { uniqueSiteRelatedCommunities } from './uniqueSiteRelatedCommunities';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import useSitesQuerySummary from 'lib/hooks/coveo/sitesHeadless/useSitesQuerySummary';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import { getSitesHeadlessFacets } from 'lib/utils/coveo/sitesHeadless/sitesHeadlessFacets';
import { RootState } from 'src/store/store';
import {
  clearCommunity,
  setCommunities,
  setCoords,
  setNationwideTotal,
  toggleCommunity,
} from '../../features/locationSearchSlice';

const FACET_KEYS: LocationSearchFacetKey[] = ['condition', 'status', 'ageGroup', 'language'];

function LocationSearch(props: SearchLocationHeroProps) {
  const dispatch = useDispatch();
  const {
    coords,
    locationLabel,
    sortByDistance,
    selectedCommunities,
    nationwideTotal,
    communities,
    hasSearched,
    searchResetVersion,
  } = useSelector((state: RootState) => state.locationSearch);
  const [selectedDistanceLabel, setSelectedDistanceLabel] = useState<string | null>(null);
  const [distanceMiles, setDistanceMiles] = useState<number | undefined>(undefined);
  const [facetControllers, setFacetControllers] = useState<
    LocationSearchFacetControllers | undefined
  >(undefined);
  const hasUserSetDistance = useRef(false);
  const lastSearchResetVersion = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      setFacetControllers(mapSitesFacetsToControllers(getSitesHeadlessFacets()));
    } catch {
      setFacetControllers(undefined);
    }
  }, []);

  const searchOptions = useMemo(() => {
    const options: {
      latitude?: number;
      longitude?: number;
      sortByDistance: boolean;
      communities?: string[];
      distanceMiles?: number;
    } = {
      latitude: coords?.lat,
      longitude: coords?.lng,
      sortByDistance,
      communities: selectedCommunities.length > 0 ? selectedCommunities : undefined,
    };

    if (distanceMiles != null) {
      options.distanceMiles = distanceMiles;
    }

    return options;
  }, [coords, sortByDistance, selectedCommunities, distanceMiles]);

  const { state, isLoading } = useSitesResultList(sitesHeadlessEngine, searchOptions);
  const queryTotal = useSitesQuerySummary(sitesHeadlessEngine);

  useEffect(() => {
    if (!hasSearched && queryTotal > 0) {
      dispatch(setNationwideTotal(queryTotal));
    }
  }, [dispatch, hasSearched, queryTotal]);

  useEffect(() => {
    dispatch(setCommunities(uniqueSiteRelatedCommunities(state)));
  }, [dispatch, state]);

  const distanceOptions = useMemo(
    () => getLocationSearchDistanceOptions(props.fields),
    [props.fields]
  );

  const applyDefaultDistance = useCallback(() => {
    const selection = resolveDefaultDistanceSelection(
      props.fields?.defaultDistance,
      distanceOptions
    );
    if (!selection) {
      return;
    }
    setSelectedDistanceLabel(selection.label);
    setDistanceMiles(selection.miles);
  }, [props.fields?.defaultDistance, distanceOptions]);

  const clearAllFilterState = useCallback(() => {
    setSelectedDistanceLabel(null);
    setDistanceMiles(undefined);
    hasUserSetDistance.current = false;
    dispatch(clearCommunity());

    FACET_KEYS.forEach((key) => {
      const facet = facetControllers?.[key] as Facet | undefined;
      if (facet?.state.hasActiveValues) {
        facet.deselectAll();
      }
    });

    if (typeof window !== 'undefined') {
      const { executeSearch } = loadSearchActions(sitesHeadlessEngine);
      const { logInterfaceChange } = loadSearchAnalyticsActions(sitesHeadlessEngine);
      sitesHeadlessEngine.dispatch(executeSearch(logInterfaceChange()));
    }
  }, [dispatch, facetControllers]);

  const handleLocationSearch = useCallback(
    (nextCoords: LocationSearchCoords, locality?: string) => {
      dispatch(setCoords({ ...nextCoords, locationLabel: locality }));
      if (!hasUserSetDistance.current) {
        applyDefaultDistance();
      }
    },
    [dispatch, applyDefaultDistance]
  );

  const handleCommunitySelect = (nextCommunity: string) => {
    dispatch(toggleCommunity(nextCommunity));
  };

  const handleDistanceChange = useCallback(
    (distanceLabel: string | null, nextDistanceMiles: number | undefined) => {
      hasUserSetDistance.current = true;
      setSelectedDistanceLabel(distanceLabel);
      setDistanceMiles(nextDistanceMiles);
    },
    []
  );

  useEffect(() => {
    if (!coords) {
      setSelectedDistanceLabel(null);
      setDistanceMiles(undefined);
    }
  }, [coords]);

  useEffect(() => {
    if (searchResetVersion === 0 || searchResetVersion === lastSearchResetVersion.current) {
      return;
    }
    lastSearchResetVersion.current = searchResetVersion;
    clearAllFilterState();
  }, [searchResetVersion, clearAllFilterState]);

  const handleClearAllFilters = useCallback(() => {
    clearAllFilterState();
  }, [clearAllFilterState]);

  return (
    <>
      <SearchLocationHero
        {...props}
        nationwideTotal={nationwideTotal}
        communities={communities}
        selectedCommunities={selectedCommunities}
        onCommunitySelect={handleCommunitySelect}
        onLocationSearch={handleLocationSearch}
      />
      <LocationSearchFilterBar
        {...props}
        sitesFoundCount={queryTotal}
        hasLocationContext={Boolean(coords)}
        selectedDistanceLabel={selectedDistanceLabel}
        onDistanceChange={handleDistanceChange}
        facetControllers={facetControllers}
        hasCommunityFilter={selectedCommunities.length > 0}
        onClearAllFilters={handleClearAllFilters}
      />
      <MapCoveoResultToCard
        results={state}
        isLoading={isLoading}
        fields={props.fields}
        coords={coords}
        distanceMiles={distanceMiles}
        hasLocationContext={Boolean(coords)}
        searchLocality={locationLabel ?? undefined}
        onClearAllFilters={handleClearAllFilters}
      />
    </>
  );
}

export default LocationSearch;
